/**
 * Photo file-name classifier.
 *
 * Leading Edge's technical head curates `Photos for Report/` by renaming each
 * photo according to one of two conventions documented in:
 *   - docs/PHOTO_NAMING.md       — the convention itself
 *   - docs/CER_PV_CHECKLIST.md   — the canonical clause IDs the non-compliance names reference
 *
 * Two filename patterns are observed in the wild:
 *
 *   Standard evidence:
 *     <JobID>_<positional-subject>[_<extra>][ (<index>)]
 *     e.g. `PVD6710073_Inverter`
 *          `PVD6710073_DC isolator before_Roof (1)`
 *          `PVD6710073_DC isolator during_Roof_Not accessible`
 *
 *   Non-compliance:
 *     <JobID>_<CATEGORY><CODE>[_<description>]
 *     e.g. `PVD6710073_WIRING2.1.1_array wiring not secured`
 *          `PVD6710073_WIRING24_Not sealed`
 *          `PVD6710073_SIGN2.1`            (no description)
 *
 *   Recommendation (free-text observation, not a clause finding):
 *     <JobID>_<CATEGORY>_RECs_<description>
 *     e.g. `PVD6710073_GENERAL_RECs_Cracked tiles`
 *          `PVD6710073_GENERAL_RECs_Damaged tiles`
 *     "RECs" = recommendations. Category names the area the recommendation is about.
 *     Description is unstructured text written by the technical head.
 *
 * `<JobID>` is `PVD<digits>` or `BTY<digits>`. `<CATEGORY>` matches the
 * categories from the CER PV checklist; `<CODE>` is digits with optional
 * dot-separated sub-numbers (e.g. `24`, `2.1`, `1.1.1.1`).
 */
/**
 * Categories used as the prefix on CER PV non-compliance clause IDs.
 * Drawn from `docs/CER_PV_CHECKLIST.md`.
 */
export const CLAUSE_CATEGORIES = [
    'CEC',
    'CER',
    'COMPONENTS',
    'D',
    'DC',
    'DOC',
    'GENERAL',
    'INSTALL',
    'MECH',
    'PROTECTION',
    'SIGN',
    'WIRING',
];
const CLAUSE_CATEGORY_SET = new Set(CLAUSE_CATEGORIES);
/**
 * Standard photo subjects from `T27 photo naming.docx`. Filenames matching
 * one of these are treated as positive evidence; others fall through to
 * `unknown`.
 */
export const STANDARD_PHOTO_SUBJECTS = [
    'DC isolator before_Roof',
    'DC isolator during_Roof',
    'DC isolator during_Roof_Not accessible',
    'DC isolator after_Roof',
    'DC isolator before_Ground',
    'DC isolator during_Ground',
    'DC isolator after_Ground',
    'Junction box before_Roof',
    'Junction box during_Roof',
    'Junction box after_Roof',
    'Inverter',
    'Inverter info',
    'Array',
    'Switchboard',
    'DC Isolator info',
    'Model serial number',
    'Model make and model',
    'Signed Report',
    'Signed acknowledgement & consent',
    'Signed Advise Given',
    'Notification Of Issues',
    'Notified owner of shading losses',
];
const STANDARD_PHOTO_SUBJECT_SET = new Set(STANDARD_PHOTO_SUBJECTS);
const JOB_ID_PREFIX = /^(?<jobId>(?:PVD|BTY)\d+)_/i;
const NON_COMPLIANCE_PATTERN = /^(?<cat>[A-Za-z]+)(?<code>\d+(?:\.\d+)*)(?:_(?<description>.+))?$/;
const RECOMMENDATION_PATTERN = /^(?<cat>[A-Za-z]+)_RECs_(?<description>.+)$/i;
const TRAILING_INDEX_PATTERN = /^(?<subject>.*?)\s*\((?<idx>\d+)\)$/;
/**
 * Recover every clause reference encoded in a curated finding filename.
 *
 * The original convention used one clause followed by an underscore, but the
 * live Dropbox also contains whitespace descriptions (`WIRING15 EPS plug`),
 * shared-category shorthand (`SIGN11 & 12`), and multiple full references
 * (`CEC12_WIRING20_More than 300mm`). One photo is valid evidence for every
 * referenced clause, so callers that build audits should use this plural API.
 */
export function extractNonComplianceFindings(filename) {
    const stem = stripExtension(filename);
    const jobMatch = JOB_ID_PREFIX.exec(stem);
    // Tranche 48's technical-head workflow often strips the job prefix after
    // moving photos into a job-scoped `Photos for Report` folder. The enclosing
    // folder already supplies identity, so an unprefixed `SIGN2.1.jpg` is just as
    // unambiguous there as `PVD123_SIGN2.1.jpg`.
    const jobId = jobMatch?.groups?.['jobId']?.toUpperCase() ?? '';
    const rest = jobMatch ? stem.slice(jobMatch[0].length).trim() : stem.trim();
    if (!rest || RECOMMENDATION_PATTERN.test(rest))
        return [];
    const categories = [...CLAUSE_CATEGORIES].sort((a, b) => b.length - a.length).join('|');
    const clauseRe = new RegExp(`(?:^|[_\\s&])(?<cat>${categories})(?<code>\\d+(?:\\.\\d+)*)`, 'gi');
    const matches = Array.from(rest.matchAll(clauseRe));
    if (matches.length === 0)
        return [];
    const last = matches[matches.length - 1];
    const lastEnd = (last.index ?? 0) + last[0].length;
    const description = rest
        .slice(lastEnd)
        .replace(/^[_\s-]+/, '')
        .trim() || undefined;
    // Some curated photos explicitly record a pass using `<clause>_COMPLIANT`.
    // They are evidence, but must never create a failed checklist response.
    if (description && /^compliant\b/i.test(description))
        return [];
    const findings = matches.map((m) => {
        const category = m.groups['cat'].toUpperCase();
        const code = m.groups['code'];
        return {
            kind: 'non-compliance',
            jobId,
            category,
            code,
            clauseId: `${category}${code}`,
            ...(description ? { description } : {}),
            filename,
        };
    });
    // Technical heads commonly abbreviate a second clause as `SIGN11 & 12`.
    const first = matches[0];
    const firstCategory = first.groups['cat'].toUpperCase();
    const ampRe = /&\s*(\d+(?:\.\d+)*)/g;
    for (const m of rest.matchAll(ampRe)) {
        const code = m[1];
        const clauseId = `${firstCategory}${code}`;
        if (!findings.some((f) => f.clauseId === clauseId)) {
            findings.push({
                kind: 'non-compliance',
                jobId,
                category: firstCategory,
                code,
                clauseId,
                ...(description ? { description } : {}),
                filename,
            });
        }
    }
    return findings;
}
/** Strip `.jpg`/`.png`/etc. Tolerant: returns input unchanged if no extension. */
function stripExtension(name) {
    return name.replace(/\.[A-Za-z0-9]+$/, '');
}
/**
 * Classify a single photo filename.
 *
 * Pure function — no I/O, no validation against the live clause table (the
 * caller can do that with `clauseId` against the codebook in
 * `docs/CER_PV_CHECKLIST.md` once it's encoded as a TS const).
 */
export function classifyPhotoFilename(filename) {
    const stem = stripExtension(filename);
    const jobMatch = JOB_ID_PREFIX.exec(stem);
    if (!jobMatch?.groups?.['jobId']) {
        return { kind: 'unknown', filename };
    }
    const jobId = jobMatch.groups['jobId'].toUpperCase();
    const rest = stem.slice(jobMatch[0].length).trim();
    if (rest.length === 0) {
        return { kind: 'unknown', jobId, filename };
    }
    const findings = extractNonComplianceFindings(filename);
    if (findings[0])
        return findings[0];
    if (/^[A-Za-z]+\d+(?:\.\d+)*[_\s-]+COMPLIANT\b/i.test(rest)) {
        return { kind: 'standard', jobId, subject: rest, isKnownSubject: false, filename };
    }
    // Try non-compliance first. The pattern is strict: <UPPER+><digits>...
    // No space between category and digit, which means "DC isolator before…"
    // never matches (space stops the [A-Za-z]+ before any digit).
    const ncMatch = NON_COMPLIANCE_PATTERN.exec(rest);
    if (ncMatch?.groups) {
        const cat = ncMatch.groups['cat'].toUpperCase();
        if (CLAUSE_CATEGORY_SET.has(cat)) {
            const code = ncMatch.groups['code'];
            const description = ncMatch.groups['description']?.trim();
            return {
                kind: 'non-compliance',
                jobId,
                category: cat,
                code,
                clauseId: `${cat}${code}`,
                ...(description ? { description } : {}),
                filename,
            };
        }
        // Fell through — looked like non-compliance but category wasn't recognised.
        // Treat as standard (unknown subject) so the photo still surfaces.
    }
    // Recommendation: <CATEGORY>_RECs_<freetext>. Free-text observation, not a clause finding.
    const recMatch = RECOMMENDATION_PATTERN.exec(rest);
    if (recMatch?.groups) {
        const cat = recMatch.groups['cat'].toUpperCase();
        const description = recMatch.groups['description'].trim();
        return {
            kind: 'recommendation',
            jobId,
            category: cat,
            isKnownCategory: CLAUSE_CATEGORY_SET.has(cat),
            description,
            filename,
        };
    }
    // Standard subject. Strip trailing "(N)" for index.
    let subject = rest;
    let index;
    const idxMatch = TRAILING_INDEX_PATTERN.exec(subject);
    if (idxMatch?.groups) {
        subject = idxMatch.groups['subject'].trim();
        index = Number.parseInt(idxMatch.groups['idx'], 10);
    }
    // If the only "subject" is the auto-numbered shot index (e.g. raw `_ (19)`),
    // there's no semantic name — surface as unknown.
    if (subject.length === 0) {
        return { kind: 'unknown', jobId, filename };
    }
    return {
        kind: 'standard',
        jobId,
        subject,
        isKnownSubject: STANDARD_PHOTO_SUBJECT_SET.has(subject),
        ...(index !== undefined ? { index } : {}),
        filename,
    };
}
/**
 * Convenience: classify a list of filenames and bucket the results.
 * The caller can iterate buckets without doing the kind-switch themselves.
 */
export function bucketPhotoFilenames(filenames) {
    const nonCompliance = [];
    const recommendations = [];
    const standard = [];
    const unknown = [];
    for (const f of filenames) {
        const findings = extractNonComplianceFindings(f);
        if (findings.length > 0) {
            nonCompliance.push(...findings);
            continue;
        }
        const c = classifyPhotoFilename(f);
        switch (c.kind) {
            case 'non-compliance':
                nonCompliance.push(c);
                break;
            case 'recommendation':
                recommendations.push(c);
                break;
            case 'standard':
                standard.push(c);
                break;
            case 'unknown':
                unknown.push(c);
                break;
        }
    }
    return { nonCompliance, recommendations, standard, unknown };
}
//# sourceMappingURL=photo-naming.js.map